<template>
    <div class="vehicle-info">
        <h1>{{ title }}</h1>

        <VehicleLocation :vehicle="vehicle" class="vehicle-info__location" :with-type="true" />

        <ul class="summary-bullets">
            <VehicleField type="value" :vehicle="vehicle" field="sleepsNumber" />
            <VehicleField type="value" :vehicle="vehicle" field="sitsNumber" />
            <VehicleField type="value" :vehicle="vehicle" field="petsAllowed" />
        </ul>

        <div class="vehicle-info__row">
            <div v-if="vehicle.reviewCount > 0" class="vehicle-info__rating">
                <svg-icon name="star" />
                <a :href="generateLink('vehicle-reviews')" @click.prevent="$emit('link-clicked', 'vehicle-reviews')">
                    {{ rating }}
                </a>
                <span class="vehicle-info__dot" />
            </div>

            <div v-if="vehicle.owner" class="vehicle-info__owner">
                <a :href="generateLink('vehicle-owner')" @click.prevent="$emit('link-clicked', 'vehicle-owner')">
                    {{ $t('vehicle_info.rented_by', { name: vehicle.owner.firstName }) }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { getVehicleFieldFormattedValue, getVehicleFieldValue } from '~/config/vehicleFields';
import VehicleLocation from '~/components/vehicles/VehicleLocation';
import VehicleField from '~/components/vehicle/VehicleField';

export default {
    name: 'VehicleInfo',
    components: { VehicleField, VehicleLocation },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
    },
    computed: {
        title() {
            const ownerName = this.vehicle.owner?.isOrganizationRepresentative ? '' : this.vehicle.owner.firstName;

            return this.$tc('vehicle_info.title', ownerName ? 2 : 1, {
                vehicleBrand: getVehicleFieldValue(this.vehicle, 'brand'),
                type: getVehicleFieldFormattedValue(this.vehicle, 'subType'),
                ownerName,
            });
        },
        rating() {
            const { averageRating, reviewCount } = this.vehicle;

            return this.$tc('vehicle_info.rating', reviewCount, {
                averageRating: averageRating.toFixed(averageRating % 1 === 0 ? 0 : 1),
                reviewCount,
            });
        },
    },
    methods: {
        generateLink(sectionName) {
            const fullPath = this.$route.fullPath.replace(/#.*/, ''); // remove hash value

            return this.$config.siteUrl ? `${this.$config.siteUrl}${fullPath}#${sectionName}` : `#${sectionName}`;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.vehicle-info {
    text-align: left;
    width: 100%;
    margin-bottom: 24px;
    @include font-size(16px);

    @include lg {
        margin-bottom: 32px;
    }

    h1 {
        @include font-size(24px);
        font-weight: 600;
        line-height: 30px;
        margin-bottom: 16px;

        @include lg {
            @include font-size(32px);
            line-height: 38px;
        }
    }

    &__row {
        display: flex;
        align-items: center;
        justify-content: space-between;

        @include lg {
            justify-content: flex-start;
        }
    }

    .summary-bullets {
        display: flex;
        margin-bottom: 16px;
        @include font-size(14px);

        @include lg {
            display: none;
        }
    }

    &__location {
        margin-bottom: 16px;

        @include lg {
            display: none;
        }
    }

    &__dot {
        display: none;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: $primary;
        margin: 0 16px;

        @include lg {
            display: inline-flex;
        }
    }

    &__rating {
        display: flex;
        align-items: center;
        white-space: nowrap;

        .svg-icon {
            height: 24px;
            width: 24px;
            color: $primary;
            margin-right: 8px;
        }
    }

    &__owner {
        display: none;

        @include lg {
            display: flex;
            align-items: center;
            white-space: nowrap;
        }
    }
}
</style>
